import { useRef } from 'react'
import type { AdminDeckDto } from '@lunary/api-client'
import type { Selection } from './card-form'
import { DropSlot } from './drop-slot'
import { useAddCustomCard, useUploadCustomImage } from './queries'
import { ACCEPT } from './use-file-drop'
import { useUploadQueue } from './use-upload-queue'
import styles from './decks.module.css'

type Props = { deck: AdminDeckDto; uploadsEnabled: boolean; selection: Selection | null; onSelect: (next: Selection) => void }

const nameOf = (file: File) => file.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ').trim().slice(0, 60) || 'Новая карта'

export function CustomSlots({ deck, uploadsEnabled, selection, onSelect }: Props) {
  const add = useAddCustomCard(deck.id)
  const upload = useUploadCustomImage(deck.id)
  const seen = useRef(new Set<string>())
  const single = useRef<HTMLInputElement>(null)
  const target = useRef<string | null>(null)
  const queue = useUploadQueue(async (key, file) => {
    if (key !== 'new') return upload.mutateAsync({ id: key, file })
    const known = new Set([...deck.custom_cards.map((c) => c.id), ...seen.current])
    const next = await add.mutateAsync({ name: nameOf(file) })
    const created = next.custom_cards.find((c) => !known.has(c.id))
    if (!created) return
    seen.current.add(created.id)
    await upload.mutateAsync({ id: created.id, file })
  })
  const locked = !uploadsEnabled || queue.busy

  return (
    <>
      {deck.custom_cards.length > 0 && <div className={styles.divider} />}
      {deck.custom_cards.map((c) => (
        <DropSlot
          key={c.id}
          name={c.name}
          label={c.name.charAt(0).toUpperCase()}
          thumb={c.image?.thumb_url}
          pressed={selection?.kind === 'custom' && selection.id === c.id}
          state={queue.state.get(c.id)}
          badge={<span className={styles.own}>своя</span>}
          dropDisabled={locked}
          ariaLabel={`${c.name}, своя карта`}
          onClick={() => onSelect({ kind: 'custom', id: c.id })}
          onFiles={(files) => {
            if (files[0]) queue.run([{ key: c.id, file: files[0] }])
          }}
          tools={
            <button
              type="button"
              aria-label={`${c.name}: ${c.image ? 'заменить картинку' : 'загрузить картинку'}`}
              title={c.image ? 'Заменить' : 'Загрузить'}
              disabled={locked}
              onClick={() => {
                target.current = c.id
                single.current?.click()
              }}
            >
              ↻
            </button>
          }
        />
      ))}
      <DropSlot
        name={queue.busy ? 'Добавляю…' : 'Добавить'}
        label="+"
        empty
        pressed={selection?.kind === 'new'}
        state={queue.state.get('new')}
        dropDisabled={locked}
        ariaLabel="Добавить свою карту"
        onClick={() => onSelect({ kind: 'new' })}
        onFiles={(files) => queue.run(files.map((file) => ({ key: 'new', file })))}
      />
      <input
        ref={single}
        type="file"
        accept={ACCEPT}
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0]
          const id = target.current
          e.target.value = ''
          if (file && id) queue.run([{ key: id, file }])
        }}
      />
      {queue.report.length > 0 && (
        <ul className={`${styles.queue} ${styles.fullRow}`} aria-live="polite">
          {queue.report.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
    </>
  )
}
